import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Coins, Check, Sparkles, Zap, Crown, Loader2, Gift } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Credits = () => {
  const { getAuthHeaders, subscription, refreshSubscription } = useAuth();
  const [loading, setLoading] = useState(null);
  const [promoCode, setPromoCode] = useState('');
  const [redeemingPromo, setRedeemingPromo] = useState(false);
  const [promoMessage, setPromoMessage] = useState('');
  const [promoError, setPromoError] = useState('');

  const creditPacks = [
    { id: 'pack_100', name: '100 Credits', credits: 100, price: 9, articles: '~40 articles' },
    { id: 'pack_500', name: '500 Credits', credits: 500, price: 39, articles: '~200 articles', popular: true },
    { id: 'pack_1000', name: '1,000 Credits', credits: 1000, price: 69, articles: '~400 articles' },
  ];

  const subscriptionTiers = [
    { tier: 'starter', name: 'Starter', price: 29, features: ['10 articles/month', '100 credits', 'AI Assistant', 'Platform publishing', 'Email support'], icon: Zap },
    { tier: 'professional', name: 'Professional', price: 79, features: ['50 articles/month', '500 credits', 'Voice AI', 'All integrations', 'Priority support'], icon: Sparkles, popular: true },
    { tier: 'enterprise', name: 'Enterprise', price: 199, features: ['200 articles/month', '2,000 credits', 'Custom workflows', 'Dedicated support', 'API access'], icon: Crown },
  ];

  const handlePurchase = async (type, id) => {
    setLoading(id);
    try {
      const payload = type === 'credits' 
        ? { package_id: id, origin_url: window.location.origin }
        : { subscription_tier: id, origin_url: window.location.origin };
      const response = await axios.post(`${API}/payments/checkout`, payload, { headers: getAuthHeaders() });
      // Backend returns the Stripe URL as `checkout_url` (not `url`) — reading the
      // wrong key sent users to /undefined instead of Stripe checkout (EV-1).
      window.location.href = response.data.checkout_url;
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to start checkout');
      setLoading(null);
    }
  };

  const handleRedeemPromo = async (e) => {
    e.preventDefault();
    if (!promoCode.trim()) return;
    setRedeemingPromo(true);
    setPromoMessage('');
    setPromoError('');
    try {
      const response = await axios.post(`${API}/promo/redeem`, { code: promoCode.toUpperCase() }, { headers: getAuthHeaders() });
      setPromoMessage(response.data.message);
      setPromoCode('');
      await refreshSubscription();
    } catch (err) {
      setPromoError(err.response?.data?.detail || 'Invalid code');
    } finally {
      setRedeemingPromo(false);
    }
  };

  return (
    <div className="fade-in" data-testid="credits-page">
      <div className="mb-8">
        <h1 className="font-serif text-3xl mb-1">Credits & Billing</h1>
        <p className="text-sm text-[var(--color-muted)]">Manage your subscription and purchase credits</p>
      </div>

      {/* Current Status */}
      <div className="card p-6 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-label text-[var(--color-muted)]">CURRENT PLAN</p>
            <p className="font-display text-2xl font-light capitalize mt-1">{subscription?.tier || 'Free'}</p>
          </div>
          <div className="text-right">
            <p className="font-label text-[var(--color-muted)]">CREDITS</p>
            <div className="flex items-center gap-2 justify-end mt-1">
              <Coins className="w-4 h-4 text-[var(--color-coral)]" />
              <span className="font-display text-2xl font-light">{subscription?.credits_remaining?.toFixed(1) || 0}</span>
            </div>
          </div>
        </div>
        {subscription?.tier === 'free' && (
          <div className="mt-4 p-3 rounded-xl bg-[var(--color-surface)] text-xs">
            <strong>Free tier:</strong> {subscription?.article_count_lifetime || 0}/3 lifetime articles used
          </div>
        )}
      </div>

      {/* Promo Code */}
      <div className="card p-6 mb-6">
        <div className="flex items-center gap-2.5 mb-4">
          <Gift className="w-4 h-4 text-[var(--color-coral)]" />
          <h2 className="font-serif text-xl">Have a promo code?</h2>
        </div>
        <form onSubmit={handleRedeemPromo} className="flex gap-3">
          <input type="text" value={promoCode} onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
            className="input flex-1 font-mono text-sm" placeholder="Enter code (e.g., APPSUMO100)" data-testid="promo-code-input" />
          <button type="submit" disabled={redeemingPromo || !promoCode.trim()} className="btn btn-primary" data-testid="redeem-promo-btn">
            {redeemingPromo ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Redeem'}
          </button>
        </form>
        {promoMessage && <p className="mt-3 text-xs font-medium text-green-600">{promoMessage}</p>}
        {promoError && <p className="mt-3 text-xs font-medium text-[var(--color-coral)]">{promoError}</p>}
      </div>

      {/* Credit Costs */}
      <div className="card p-6 mb-6">
        <h2 className="font-serif text-xl mb-4">Credit Costs</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[{ v: '2.5', l: 'Article generation' }, { v: '0.5', l: 'SEO analysis' }, { v: '1', l: 'AI chat message' }, { v: '5', l: 'Voice session' }].map((item, i) => (
            <div key={i} className="text-center p-3 rounded-xl bg-[var(--color-surface)]">
              <p className="font-display text-xl font-light">{item.v}</p>
              <p className="text-[10px] text-[var(--color-muted)] mt-0.5">{item.l}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Credit Packs */}
      <div className="mb-12">
        <h2 className="font-serif text-xl mb-1">Buy Credit Packs</h2>
        <p className="text-xs text-[var(--color-muted)] mb-5">Credits never expire. Use them anytime.</p>
        <div className="grid md:grid-cols-3 gap-4">
          {creditPacks.map((pack) => (
            <div key={pack.id} className={`card p-6 text-center ${pack.popular ? '' : ''}`}
                 style={pack.popular ? {boxShadow: 'inset 0 0 0 2px var(--color-coral)'} : {}}
                 data-testid={`credit-pack-${pack.id}`}>
              {pack.popular && <span className="font-label text-[var(--color-coral)] mb-2 inline-block">BEST VALUE</span>}
              <div className="font-display text-3xl font-light">{pack.credits}</div>
              <p className="text-xs text-[var(--color-muted)] mb-4">credits</p>
              <div className="font-display text-2xl font-light mb-1">&#8364;{pack.price}</div>
              <p className="text-[10px] text-[var(--color-muted)] mb-4">{pack.articles}</p>
              <button onClick={() => handlePurchase('credits', pack.id)} disabled={loading === pack.id}
                className={`btn w-full ${pack.popular ? 'btn-accent' : 'btn-secondary'}`}>
                {loading === pack.id ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Buy now'}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Subscription Tiers */}
      <div>
        <h2 className="font-serif text-xl mb-1">Upgrade Your Plan</h2>
        <p className="text-xs text-[var(--color-muted)] mb-5">Get more articles and unlock premium features.</p>
        <div className="grid md:grid-cols-3 gap-5">
          {subscriptionTiers.map((tier) => {
            const Icon = tier.icon;
            const isCurrent = subscription?.tier === tier.tier;
            return (
              <div key={tier.tier} className={`pricing-card ${tier.popular ? 'featured' : ''}`} data-testid={`tier-${tier.tier}`}>
                {tier.popular && <div className="font-label text-[var(--color-coral)] mb-4">MOST POPULAR</div>}
                <div className="w-11 h-11 mx-auto rounded-full flex items-center justify-center mb-4 bg-[var(--color-surface)]">
                  <Icon className="w-5 h-5 text-[var(--color-coral)]" />
                </div>
                <div className="pricing-tier">{tier.name}</div>
                <div className="pricing-price">&#8364;{tier.price}<span>/mo</span></div>
                <div className="pricing-features">
                  {tier.features.map((feature, idx) => (
                    <div key={idx} className="pricing-feature">
                      <Check className="w-3.5 h-3.5" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
                <button onClick={() => handlePurchase('subscription', tier.tier)}
                  disabled={loading === tier.tier || isCurrent}
                  className={`btn w-full ${tier.popular ? 'btn-accent' : 'btn-secondary'}`}>
                  {loading === tier.tier ? <Loader2 className="w-4 h-4 animate-spin" /> : isCurrent ? 'Current Plan' : 'Upgrade'}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Credits;
